import _ from 'lodash'
import type { PageDefinition } from '@wix/thunderbolt-components-native'
import type { BeckyFuncLibParams, Component, FetchFn, MetricsReporter, Structure } from '@wix/thunderbolt-becky-types'
import { structure2mesh } from '@wix/thunderbolt-mesh'
import { convertSeoModel } from '@wix/advanced-seo-utils/converters'
import {
	color,
	getDisplayedId,
	getFullId,
	getItemId,
	getTemplateCompIdAndRepeaterScope,
	getFullItemId,
	getOuterItemId,
	getInnerMostItemId,
	isDisplayedOnly,
	isRepeatedComponentOfTemplate,
	getRepeaterItemsAncestorChain,
	isRepeater,
	links,
	nameUUIDFromString,
	text,
	vectorImage,
} from '@wix/thunderbolt-commons'
import type { BeckyFunctionLibrary, BeckyModel, CompInfo } from './types'
import { getDockedStyles } from './dockedLayer'
import * as functionLibraryExtensions from './functionLibraryExtensions'
import {
	getMeshOverrides,
	getMeshResult,
	getMeshResultForHoverBox,
	getMeshResultsForMasterPage,
} from './functionLibraryExtensions/mesh/meshFunctionLibrary'
import { getColumnOverrides } from './functionLibraryExtensions/mesh/components/column'
import { refApi } from '@wix/thunderbolt-refs'

const globalCache = new WeakMap()
const fetchResultsCache = new WeakMap()

const stringifyAttrs = (attrs: Record<string, string>) => {
	return Object.keys(attrs)
		.map((k) => `${k}="${attrs[k]}"`)
		.join(' ')
}

const fetchUrlFactory = (
	fetch: FetchFn,
	runAsyncAndReport: MetricsReporter['runAsyncAndReport'],
	reportError: MetricsReporter['reportError']
) => {
	return function (
		this: BeckyFunctionLibrary,
		url: string,
		fallbackValue: string,
		updateStore: (value: string | null) => void
	): void {
		let pendingRequests = globalCache.get(this)
		let resultsCache = fetchResultsCache.get(this)
		if (!pendingRequests) {
			globalCache.set(this, {})
			pendingRequests = globalCache.get(this)
		}
		if (!resultsCache) {
			fetchResultsCache.set(this, {})
			resultsCache = fetchResultsCache.get(this)
		}
		if (resultsCache[url] || pendingRequests[url]) {
			return
		}
		pendingRequests[url] = true

		updateStore(null)
		fetch(url)
			.then((res) => (res.ok ? res.text() : null))
			.catch((e) => {
				reportError(e)
				return null
			})
			.then((txt) => {
				resultsCache[url] = true
				updateStore(txt === null ? fallbackValue : txt)
			})
	}
}

const done = () => {}

const invoke = (cb: Function, ...args: Array<any>) => typeof cb === 'function' && cb(...args)

const invokeResolverFactory = (reportError: MetricsReporter['reportError']) => (
	cb: Function,
	extensionName: string,
	compId: string,
	compType: string,
	fallbackValue: any,
	compInfo: Partial<CompInfo>,
	shouldInjectRefApi: boolean,
	carmiData: any
) => {
	try {
		return cb ? cb({ ...compInfo, ...(shouldInjectRefApi && { refApi }) }, carmiData) : carmiData
	} catch (error) {
		const wrappedError = new Error(
			`Error resolving ${extensionName} for component ${compId} of type ${compType}: ${error?.message}`
		)

		console.error(wrappedError, error)
		reportError(wrappedError)

		return fallbackValue
	}
}

const cloneDeep = (value: any) => _.cloneDeep(value)

const castToNumber = (value: string) => Number(value)

const regExpMatch = (toFind: string, regExpStr: string) => toFind.match(new RegExp(regExpStr))

const rgbToHex = (rgbString: string) => {
	if (!rgbString.includes(',')) {
		return rgbString
	}

	const componentToHex = (c: string) => {
		// @ts-ignore
		const hex = c.toString(16)
		return hex.length === 1 ? '0' + hex : hex
	}

	const [r, g, b] = rgbString.split(',')
	return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

const getUndefined = () => void 0

export const seoMergeBlob = (
	originalPageData: PageDefinition['unresolvedData'],
	translatedPageData: PageDefinition['unresolvedData']
) => {
	const original = convertSeoModel(originalPageData?.advancedSeoData || '')
	const translated = convertSeoModel(translatedPageData?.advancedSeoData || '')

	const advancedSeoData = translatedPageData?.id ? translated : original
	return { ...originalPageData, ...translatedPageData, advancedSeoData: JSON.stringify(advancedSeoData) }
}

const getColorIndex = (colorName: string) => Number(colorName.split('_').pop())

const collectNestedStructure = (root: BeckyModel['structure']['string'], structure: BeckyModel['structure']) => {
	// simple BFS impl to collect entire nested tree as flat object
	const queue: Array<string> = root.components || []
	const nestedStructure: BeckyModel['structure'] = {}

	while (queue.length) {
		const childId = queue.pop() as string
		const child = structure[childId]
		nestedStructure[childId] = child
		queue.push(...(child.components || []))
	}
	return nestedStructure
}

export const removeHash = (value?: string) => (typeof value === 'string' ? value.replace('#', '') : value)

const getDraftIds: BeckyFunctionLibrary['getDraftIds'] = (item) => {
	if (item?._draftRefs) {
		const props = item._draftRefs.map(({ path = [''] }) => path[0]) // currently we only really support path of length 1
		return (_(item)
			.pick(props)
			.mapValues((value) => (_.isArray(value) ? _.map(value, removeHash) : removeHash(value)))
			.value() as unknown) as Record<string, string | Array<string>>
	}
	return null
}

const getResolvedRefs: BeckyFunctionLibrary['getResolvedRefs'] = (refs, refMap) => refs.map((refId) => refMap[refId])

const createFunctionLibrary = ({ metricsReporter, fetch }: BeckyFuncLibParams): BeckyFunctionLibrary => {
	const { runAndReport, runAsyncAndReport, reportError } = metricsReporter

	const { buildSvgUrl, getSvgBaseUrl } = vectorImage.buildSvgUrlFactory()

	return {
		isArray: _.isArray,
		sortBy: _.sortBy,
		trim: (str: string) => str.trim(),
		removeHash,
		fixStructureMetaData: (structure: Structure, originalCompMetaData: Component['metaData']): Structure =>
			_.mapValues(structure, (comp) => ({
				...comp,
				metaData: { ...originalCompMetaData, ...comp?.metaData },
			})),
		replace: (value: string, searchValue: string | RegExp, replaceValue: string) =>
			value.replace(searchValue, replaceValue),
		getRegExp: (pattern: string, flags?: string) => new RegExp(pattern, flags),
		split: (str: string, delimiter: string) => str.split(delimiter),
		structure2mesh,
		getWixcodeConnection: (role: string, compId: string, controllerId: string) => ({
			controllerId,
			[role]: compId,
		}),
		kebabCase: _.kebabCase,
		includes: (str: string, searchString: string) => str.includes(searchString),
		merge: (objects: Array<any>) => _.merge({}, ...objects),
		mergeAndConcat: (objects: Array<any>) =>
			_.mergeWith({}, ...objects, (objValue: any, srcValue: any) => {
				if (_.isArray(objValue)) {
					return objValue.concat(srcValue)
				}
			}),
		parseInt: (val: string) => parseInt(val, 10),
		parseJSON: (val: string) => {
			if (!val) {
				return val
			}

			try {
				return JSON.parse(val)
			} catch (e) {
				if (process.env.NODE_ENV === 'development') {
					console.warn('parseJSON failed parsing value:', val)
				}
				return null
			}
		},
		serializeJSON: (val: object) => JSON.stringify(val),
		doubleQuotesEscapeForIE: (str: string) => str.replace(/''/g, "'"),
		encodeURIComponent,
		mathFloor: Math.floor,
		parseFloat,
		getDockedStyles,
		fetchUrl: fetchUrlFactory(fetch, runAsyncAndReport, reportError),
		buildSvgUrl,
		getSvgBaseUrl,
		stringifyAttrs,
		invoke,
		getDeletedItems: (uniqueIdentifier, getCurrentValue, getPreviousVersionStore) => {
			const store = getPreviousVersionStore()
			const previous = store.get(uniqueIdentifier) || {}
			const currentValue = getCurrentValue()
			const deletedItems = _.omitBy(previous, (val, key) => _.has(currentValue, key))
			store.set(uniqueIdentifier, { ...currentValue })

			return deletedItems
		},
		invokeResolver: invokeResolverFactory(reportError),
		regExpMatch,
		castToNumber,
		getUndefined,
		rgbToHex,
		getColorIndex,
		...vectorImage,
		...color,
		...links,
		...text,
		getFullId,
		getItemId,
		getTemplateCompIdAndRepeaterScope,
		getFullItemId,
		getOuterItemId,
		getDisplayedId,
		isDisplayedOnly,
		isRepeatedComponentOfTemplate,
		getRepeaterItemsAncestorChain,
		getInnerMostItemId,
		isRepeater,
		runAsyncAndReport,
		runAndReport,
		reportError: (msg: string) => {
			reportError(new Error(msg))
		},
		...functionLibraryExtensions,
		seoMergeBlob,
		getMeshResult,
		getMeshResultsForMasterPage,
		getMeshResultForHoverBox,
		getMeshOverrides,
		nameUUIDFromString,
		done,
		getDraftIds,
		getResolvedRefs,
		cloneDeep,
		collectNestedStructure,
		getColumnOverrides,
		loadPages: () => {},
	}
}

export { createFunctionLibrary }
